import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { useLanguage } from '../../contexts/LanguageContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiSave } = FiIcons;

const LinkSettings = () => {
  const navigate = useNavigate();
  const { t, language, registrationLinks, updateRegistrationLinks } = useLanguage();
  
  const [links, setLinks] = useState({
    TOP_DISTINGUISHED: '',
    PASSED: '',
    FAIL: ''
  });
  
  const [buttonTexts, setButtonTexts] = useState({
    TOP_DISTINGUISHED: '',
    PASSED: '',
    FAIL: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Initialize form with current links
    setLinks({
      TOP_DISTINGUISHED: registrationLinks.TOP_DISTINGUISHED || '',
      PASSED: registrationLinks.PASSED || '',
      FAIL: registrationLinks.FAIL || ''
    });
    
    // Initialize button text with default translations or custom values
    setButtonTexts({
      TOP_DISTINGUISHED: t('registerTeamText'),
      PASSED: t('registerAttendText'),
      FAIL: t('registerAttendText')
    });
  }, [registrationLinks, t]);

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    const newErrors = {};
    
    // Validate URLs
    if (!links.TOP_DISTINGUISHED || !validateUrl(links.TOP_DISTINGUISHED)) {
      newErrors.TOP_DISTINGUISHED = 'invalidUrl';
    }
    
    if (!links.PASSED || !validateUrl(links.PASSED)) {
      newErrors.PASSED = 'invalidUrl';
    }
    
    if (!links.FAIL || !validateUrl(links.FAIL)) {
      newErrors.FAIL = 'invalidUrl';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }
    
    // Save the links
    updateRegistrationLinks(links);
    
    // Show success message briefly
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 3000);
    
    setLoading(false);
  };

  const handleLinkChange = (key, value) => {
    setLinks(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({
        ...prev,
        [key]: null
      }));
    }
  };

  return (
    <Layout title={t('linkSettings')}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3 rtl:space-x-reverse">
              <Button
                onClick={() => navigate('/admin')}
                variant="secondary"
                size="sm"
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
                <span>{t('back')}</span>
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('linkSettings')}
              </h1>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Top Distinguished Link */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('topDistinguishedLink')} - {buttonTexts.TOP_DISTINGUISHED}
              </label>
              <Input
                value={links.TOP_DISTINGUISHED}
                onChange={(e) => handleLinkChange('TOP_DISTINGUISHED', e.target.value)}
                placeholder="https://..."
                error={errors.TOP_DISTINGUISHED}
                dir="ltr"
                className="font-mono"
              />
            </div>

            {/* Passed Link */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('passedLink')} - {buttonTexts.PASSED}
              </label>
              <Input
                value={links.PASSED}
                onChange={(e) => handleLinkChange('PASSED', e.target.value)}
                placeholder="https://..."
                error={errors.PASSED}
                dir="ltr"
                className="font-mono"
              />
            </div>

            {/* Failed Link */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('failedLink')} - {buttonTexts.FAIL}
              </label>
              <Input
                value={links.FAIL}
                onChange={(e) => handleLinkChange('FAIL', e.target.value)}
                placeholder="https://..."
                error={errors.FAIL}
                dir="ltr"
                className="font-mono"
              />
            </div>

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4">
                {t('linkUpdated')}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-4 rtl:space-x-reverse pt-6 border-t border-gray-200">
              <Button
                type="button"
                onClick={() => navigate('/admin')}
                variant="secondary"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <span>{t('loading')}</span>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} className="w-4 h-4" />
                    <span>{t('save')}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </Layout>
  );
};

export default LinkSettings;